<script>
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { preventDefault } from 'svelte/legacy';
import { Circle } from "svelte-loading-spinners";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { v4 as uuidv4 } from "uuid";
import { decode } from "html-entities";
import * as m from "@app/paraglide/messages.js";

/* Firebase */
import { auth } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import storage from "@app/configs/storage.js";
import { collection, doc, setDoc, updateDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, uploadString } from "firebase/storage";

/* CC LIB */
import tcLib from '@cclib/lib/timecode.js'
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import frameRates from "@cclib/dict/frameRates.js";
import sccLib from "@cclib/dict/608.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";
import {
    getTrialJobSlotsRemaining,
    writeTrialLimitJob,
    TRIAL_LIMIT_MESSAGE,
} from "@app/lib/trialJobLimit.js";

// State variables
let videoFiles = $state([]);
let eventGroup = $state(
        getSelectedEventGroup([
            "subtitle",
            "translation",
            "audio description",
            "audio dubbing",
            "transcription",
        ])
);
let trgFrameRate = $state(projectState.frameRate);
let trgDropFrame = $state(projectState.dropFrame ? "true" : "false");
let processing = $state(false);
let progress = $state(0);

// Pricing constants
const EMBED_BASE_COST = 200; // $2.00 in cents
const STORAGE_COST_PER_GB = 40; // $0.40 per GB in cents

let grandTotal = $derived(videoFiles.reduce((sum, item) => sum + item.cost, 0));

const userId = auth.currentUser?.uid;
const userEmail = auth.currentUser?.email;
const teamId = authState.team ? authState.team.id : null;
const homeRef = authState.team 
    ? collection(db, "teams", teamId, "jobs") 
    : collection(db, "users", userId, "jobs");
const storageRef = ref(storage);
const uploadBasePath = teamId ? `teams/${teamId}/embed/` : `users/${userId}/embed/`;

// Get valid event groups (subtitle, translation, transcription)
function getValidEventGroups() {
    return eventGroupState.filter(eg => 
        ["subtitle", "translation", "transcription", "audio description", "audio dubbing"].includes(eg.type)
    );
}

function getSelectedEventGroup(validTypes) {
    let evGroup = eventGroupState[projectState.selected];
    if (evGroup) {
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup.id;
        } else {
            return false;
        }
    } else {
        return false;
    }
}

// Minimum 1GB, round to nearest GB
function calcCost(file) {
    const sizeGB = file.size / (1024 * 1024 * 1024);
    const billableGB = sizeGB < 1 ? 1 : Math.round(sizeGB);
    const storageCost = billableGB * STORAGE_COST_PER_GB;
    return {
        sizeGB,
        billableGB,
        storageCost,
        cost: EMBED_BASE_COST + storageCost
    };
}

function formatCents(cents) {
    return (cents / 100).toFixed(2);
}

function escapeHtml(str) {
    return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// Handle file selection
function handleFileInput(event) {
    const files = Array.from(event.currentTarget.files || []);
    files.forEach((file) => {
        const isDuplicate = videoFiles.some(item => item.file.name === file.name && item.file.size === file.size);
        if (isDuplicate) return;
        videoFiles.push({
            id: uuidv4(),
            file,
            name: file.name.replace(/\.[^/.]+$/, ""),
            ...calcCost(file),
            uploadProgress: 0,
            status: "pending"
        });
    });
    event.currentTarget.value = "";
}

function removeFile(id) {
    videoFiles = videoFiles.filter(item => item.id !== id);
}

// Sanitize custom name to only include alphanumeric characters
function sanitizeCustomName(name) {
    return name.replace(/[^a-zA-Z0-9]/g, '');
}

function buildConfirmHtml() {
    const fileRows = videoFiles.map(item => `
        <div class="mb-2">
            <div class="fw-bold text-truncate">${escapeHtml(item.file.name)}</div>
            <div class="row small">
                <div class="col">${m.embed_captions_confirm_processing_label()}</div>
                <div class="col-auto">$${formatCents(EMBED_BASE_COST)}</div>
            </div>
            <div class="row small">
                <div class="col">${m.embed_captions_confirm_storage_label({ gb: item.billableGB })}</div>
                <div class="col-auto">$${formatCents(item.storageCost)}</div>
            </div>
            <div class="row small">
                <div class="col">${m.embed_captions_pricing_subtotal_label()}</div>
                <div class="col-auto"><strong>$${formatCents(item.cost)}</strong></div>
            </div>
        </div>
    `).join("");

    return `
        <div class="text-start">
            ${videoFiles.length > 1 ? `<p class="small mb-2">${m.embed_captions_confirm_job_count({ count: videoFiles.length })}</p>` : ""}
            ${fileRows}
            <hr class="my-2">
            <div class="row mb-3">
                <div class="col"><strong>${m.embed_captions_confirm_total_label()}</strong></div>
                <div class="col-auto"><strong>$${formatCents(grandTotal)}</strong></div>
            </div>
            <p class="text-secondary small mb-0">${m.embed_captions_confirm_storage_note()}</p>
        </div>
    `;
}

function buildJobInfo(item, { jobId, batchId, displayName, frameRate, dropFrame }) {
    const now = Timestamp.fromDate(new Date());
    return {
        id: jobId,
        projectId: jobId,
        projectName: displayName,
        batchId: batchId,
        submittedBy: userEmail,
        userId: userId,
        progress: 0,
        cost: item.cost,
        embedCost: EMBED_BASE_COST,
        storageCost: item.storageCost,
        fileSizeGB: item.sizeGB,
        billableGB: item.billableGB,
        type: "embed",
        statusMsg: m.embed_captions_status_awaiting_processing(),
        status: m.embed_captions_status_submitted(),
        createdOn: now,
        updatedOn: now,
        completedOn: null,
        deleted: false,
        customName: sanitizeCustomName(displayName) || jobId,
        mediaFile: item.file.name,
        fileSize: item.file.size,
        subtitleFile: `${jobId}.mcc`,
        subtitleFileType: "mcc",
        config: {
            frameRate: frameRate,
            dropFrame: dropFrame
        }
    };
}

function uploadVideo(item, jobId, onProgress) {
    const videoRef = ref(storageRef, `${uploadBasePath}${jobId}.${item.file.name.split('.').pop()}`);
    const uploadTask = uploadBytesResumable(videoRef, item.file);
    return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
            (snapshot) => {
                onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
            },
            reject,
            resolve
        );
    });
}

// Start submission process
async function submitJob() {
    if (videoFiles.length === 0) {
        toast.push(m.embed_captions_select_video_file(), { classes: ["toast-danger"] });
        return;
    }
    if (!eventGroup) {
        toast.push(m.embed_captions_select_caption_track(), { classes: ["toast-danger"] });
        return;
    }

    // Show payment confirmation
    const confirmResult = await Swal.fire({
        ...SWAL_BASE,
        title: m.embed_captions_confirm_title(),
        html: buildConfirmHtml(),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: m.embed_captions_confirm_submit(),
        cancelButtonText: m.embed_captions_confirm_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
    });

    if (!confirmResult.isConfirmed) {
        return;
    }

    processing = true;
    progress = 2;
    videoFiles.forEach(item => {
        item.status = "pending";
        item.uploadProgress = 0;
    });

    const frameRate = parseFloat(trgFrameRate);
    const dropFrame = trgDropFrame === "true" && [23.976,24,25,30,50,60].indexOf(frameRate) === -1;

    try {
        const selectedEventGroupIndex = eventGroupState.findIndex(eg => eg.id === eventGroup);
        if (selectedEventGroupIndex === -1) {
            throw new Error(m.embed_captions_event_group_not_found());
        }

        const remainingEmbedSlots = await getTrialJobSlotsRemaining(homeRef, "embed");
        if (remainingEmbedSlots < videoFiles.length) {
            const trialBatchId = uuidv4();
            const overLimit = videoFiles.slice(remainingEmbedSlots);
            for (const item of overLimit) {
                const trialJobId = uuidv4();
                const displayName = item.name || eventGroupState[selectedEventGroupIndex].name;
                await writeTrialLimitJob(homeRef, {
                    ...buildJobInfo(item, { jobId: trialJobId, batchId: trialBatchId, displayName, frameRate, dropFrame }),
                    progress: 100,
                });
                item.status = "failed";
            }
            await Swal.fire({
                ...SWAL_BASE,
                title: TRIAL_LIMIT_MESSAGE,
                text: m.embed_captions_trial_limit_files({ remaining: remainingEmbedSlots, count: videoFiles.length }),
                icon: "error",
                confirmButtonText: m.embed_captions_ok(),
                customClass: SWAL_CLASSES.ok,
            });
            toast.push(TRIAL_LIMIT_MESSAGE, { classes: ["toast-danger"] });
            processing = false;
            progress = 0;
            return;
        }

        // Run automated checks
        let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroupState[selectedEventGroupIndex]));
        copyOfEventGroup = orderByTime(copyOfEventGroup);
        copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);
        
        progress = 4;
        
        const checkResult = await preExportCheck(copyOfEventGroup.events);
        if (checkResult.err) {
            cancelExport(selectedEventGroupIndex, checkResult.eventId);
            return;
        }
        
        progress = 6;
        
        // Apply trial text if in trial mode
        if (authState.status === "in_trial") {
            copyOfEventGroup = replaceTrialText(copyOfEventGroup);
        }
        
        copyOfEventGroup.events.forEach((event, index, events) => {
            events[index].text = wrapStyledTextWithSpan(event.text);
        });
        
        // Convert event group to MCC
        let options = new defaults.options({
            profile: "macCaption",
            formatOptions: formats.profileMapping["MacCaption 608/708"].options.encode,
            frameRate: frameRate,
            dropFrame: dropFrame,
            target_profile: "macCaption",
            source_profile: "closedCaptionProject",
            incode: "00:00:00:00",
            window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding
            }
        });
        
        const mccOutput = await encode(copyOfEventGroup, options);
        progress = 10;

        const batchId = uuidv4();
        const total = videoFiles.length;
        let completedCount = 0;
        let failedCount = 0;

        // Upload one file at a time so large uploads don't compete for bandwidth
        for (const item of videoFiles) {
            const jobId = uuidv4();
            const displayName = item.name || copyOfEventGroup.name;
            const setFileProgress = (fileProgress) => {
                item.uploadProgress = fileProgress;
                progress = 10 + 90 * ((completedCount + failedCount + fileProgress / 100) / total);
            };

            item.status = "uploading";
            let jobRegistered = false;
            try {
                await setDoc(doc(homeRef, jobId), buildJobInfo(item, { jobId, batchId, displayName, frameRate, dropFrame }));
                jobRegistered = true;

                const mccRef = ref(storageRef, `${uploadBasePath}${jobId}.mcc`);
                await uploadString(mccRef, mccOutput);

                await uploadVideo(item, jobId, setFileProgress);
                item.status = "done";
                completedCount++;
            } catch (error) {
                console.error("Upload error:", error);
                item.status = "failed";
                failedCount++;
                toast.push(m.embed_captions_upload_failed({ error: error.message }), {
                    classes: ["toast-danger"]
                });
                if (jobRegistered) {
                    await updateDoc(doc(homeRef, jobId), {
                        progress: 100,
                        statusMsg: m.embed_captions_upload_failed_status({ error: error.code || error.message }),
                        status: m.embed_captions_status_failed(),
                        completedOn: Timestamp.fromDate(new Date()),
                        updatedOn: Timestamp.fromDate(new Date())
                    }).catch(updateErr => console.error("Failed to update job status:", updateErr));
                }
            }
            progress = 10 + 90 * ((completedCount + failedCount) / total);
        }

        progress = 100;
        processing = false;

        if (failedCount === 0) {
            await Swal.fire({
                ...SWAL_BASE,
                title: m.embed_captions_submitted_title(),
                text: total > 1
                    ? m.embed_captions_submitted_text_multiple({ count: total })
                    : m.embed_captions_submitted_text(),
                icon: "success",
                confirmButtonText: m.embed_captions_ok(),
                customClass: SWAL_CLASSES.ok,
            });
            toast.push(m.embed_captions_submitted_toast(), {
                classes: ["toast-success"]
            });
            hideModal();
        } else {
            await Swal.fire({
                ...SWAL_BASE,
                title: m.embed_captions_batch_result_title(),
                text: m.embed_captions_batch_result_text({ submitted: completedCount, total, failed: failedCount }),
                icon: completedCount > 0 ? "warning" : "error",
                confirmButtonText: m.embed_captions_ok(),
                customClass: SWAL_CLASSES.ok,
            });
            // Keep only the failed files so the user can retry them
            videoFiles = videoFiles.filter(item => item.status === "failed");
        }
    } catch (error) {
        console.error("Error submitting job:", error);
        toast.push(m.embed_captions_submit_failed({ error: error.message }), {
            classes: ["toast-danger"]
        });
        processing = false;
    }
}

// Helper function to find first illegal character in event text
function findIllegalChar(text) {
    const plainText = convertToPlainText(text, "");
    for (const char of plainText) {
        // Skip space characters (charCode 32)
        if (char.charCodeAt(0) === 32) continue;
        if (sccLib.valid.indexOf(char) === -1) {
            return char;
        }
    }
    return null;
}

// Helper function to get line info (character count per line)
function getLineInfo(text) {
    const plainText = convertToPlainText(text);
    const lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });
    return lines;
}

// 608 caption constraints
const MAX_LINES = 4;
const MAX_CHARS_PER_LINE = 32;

async function preExportCheck(events) {
    let ignoreAll = false;
    const ignoreRemaining = () => {
        ignoreAll = true;
    };
    
    for (let i = 0; i < events.length; i++) {
        if (!ignoreAll) {
            if (events[i].text) {
                // Check for illegal characters first - this cannot be ignored
                const illegalChar = findIllegalChar(events[i].text);
                if (illegalChar) {
                    await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.embed_captions_illegal_char_title(),
                        text: m.embed_captions_illegal_char_text({
                            index: i + 1,
                            char: illegalChar
                        }),
                        icon: "error",
                        confirmButtonText: m.embed_captions_ok(),
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: SWAL_CLASSES.dangerOk,
                    });
                    return { err: true, eventId: i };
                }

                // Check line count - max 4 lines for 608 captions
                const lineInfo = getLineInfo(events[i].text);
                if (lineInfo.length > MAX_LINES) {
                    await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.embed_captions_too_many_lines_title(),
                        text: m.embed_captions_too_many_lines_text({
                            index: i + 1,
                            lines: lineInfo.length,
                            maxLines: MAX_LINES
                        }),
                        icon: "error",
                        confirmButtonText: m.embed_captions_ok(),
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: SWAL_CLASSES.dangerOk,
                    });
                    return { err: true, eventId: i };
                }

                // Check characters per line - max 32 characters for 608 captions

                // console.log(lineInfo);
                const longLineIndex = lineInfo.findIndex(charCount => charCount > MAX_CHARS_PER_LINE);
                // console.log("longLineIndex:", longLineIndex);
                if (longLineIndex !== -1) {
                    await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.embed_captions_line_too_long_title(),
                        text: m.embed_captions_line_too_long_text({
                            index: i + 1,
                            line: longLineIndex + 1,
                            count: lineInfo[longLineIndex],
                            maxChars: MAX_CHARS_PER_LINE
                        }),
                        icon: "error",
                        confirmButtonText: m.embed_captions_ok(),
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: SWAL_CLASSES.dangerOk,
                    });
                    return { err: true, eventId: i };
                }

                if (isNaN(events[i].start)) {
                    if (await alertUser(m.embed_captions_event_missing_start({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (isNaN(events[i].end)) {
                    if (await alertUser(m.embed_captions_event_missing_end({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (events[i].start === events[i].end) {
                    if (await alertUser(m.embed_captions_event_same_time({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (events[i].start > events[i].end) {
                    if (await alertUser(m.embed_captions_event_start_after_end({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                } else if (i > 0 && events[i].start < events[i - 1].end) {
                    /* Adding check in case overlap is less than a frame */
                    if (events[i - 1].end - events[i].start < 1 / parseFloat(trgFrameRate)) {
                        events[i].start = events[i - 1].end;
                    } else if (await alertUser(m.embed_captions_event_overlap({ index: i + 1 }), ignoreRemaining)) {
                        return { err: true, eventId: i };
                    }
                }
            } else {
                if (await alertUser(m.embed_captions_event_missing_text({ index: i + 1 }), ignoreRemaining)) {
                    return { err: true, eventId: i };
                }
            }
        }
    }

    return { err: false };
}

function alertUser(msg, onIgnoreAll) {
    let response = Swal.fire({
        ...SWAL_BASE,
        titleText: m.embed_captions_precheck_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: m.embed_captions_precheck_abort(),
        denyButtonText: m.embed_captions_precheck_ignore(),
        cancelButtonText: m.embed_captions_precheck_ignore_all(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else if (result.isDenied) {
            return false;
        } else if (result.isDismissed) {
            onIgnoreAll();
            return false;
        }
    });

    return response;
}

function cancelExport(eventGroupId, eventId) {
    toast.push(m.embed_captions_aborted_toast(), {
        classes: ["toast-warning"],
    });

    projectState.selected = eventGroupId;
    eventGroupState[projectState.selected].selected = [eventId];   

    setTimeout(() => {
        const selectedIndex = eventGroupState[projectState.selected].selected[0];
        window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
            detail: { index: selectedIndex }
        }));

        if (lockState.video && player){
            player.currentTime = eventGroupState[projectState.selected].events[selectedIndex].start;
        }
    }, 50);

    processing = false;
    hideModal();
}
</script>
<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'embedCaptionsExport'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    id="ExportModal">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{m.embed_captions_title()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.embed_captions_close_label()}
                    onclick={hideModal}>
                </button>
            </div>
            <div class="modal-body">
                <p><i class="bi bi-info-circle-fill"></i> {m.embed_captions_intro()}</p>
                <div class="p-3 bg-dark rounded my-3">
                    <p class="small text-light m-0">
                        {m.embed_captions_storage_notice_prefix()} <strong>{m.embed_captions_storage_notice_strong()}</strong>
                    </p>
                </div>
                

                <form onsubmit={preventDefault(() => submitJob())}>
                    <div class="card shadow-sm mb-4">
                        <div class="card-header bg-light">
                            <h5 class="card-title mb-0">{m.embed_captions_media_caption_selection()}</h5>
                        </div>
                        <div class="card-body">
                            <!-- Video File Upload -->
                            <div class="mb-3">
                                <label for="videoFileInput" class="form-label fw-bold">
                                    {m.embed_captions_video_files_label()}
                                </label>
                                <div class="input-group">
                                    <input 
                                        type="file" 
                                        class="form-control" 
                                        id="videoFileInput"
                                        accept=".mov,.mp4,.gxf,.mpeg,.mxf,.mpg" 
                                        multiple
                                        onchange={handleFileInput}
                                        disabled={processing}
                                    />
                                </div>
                                <div class="form-text">{m.embed_captions_video_files_help()}</div>
                                {#if videoFiles.length > 0}
                                    <ul class="list-group mt-2">
                                        {#each videoFiles as item (item.id)}
                                            <li class="list-group-item">
                                                <div class="d-flex align-items-center mb-2">
                                                    {#if item.status === "done"}
                                                        <span class="badge bg-success me-2"><i class="bi bi-check-lg"></i></span>
                                                    {:else if item.status === "failed"}
                                                        <span class="badge bg-danger me-2"><i class="bi bi-x-lg"></i></span>
                                                    {:else if item.status === "uploading"}
                                                        <span class="badge bg-primary me-2"><i class="bi bi-cloud-upload"></i></span>
                                                    {:else}
                                                        <span class="badge bg-secondary me-2"><i class="bi bi-film"></i></span>
                                                    {/if}
                                                    <small class="text-truncate">{item.file.name}</small>
                                                    <small class="text-muted ms-2">
                                                        {m.embed_captions_file_size_gb({ size: item.sizeGB.toFixed(2) })}
                                                    </small>
                                                    <small class="ms-auto fw-bold">${formatCents(item.cost)}</small>
                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-link text-danger ms-2 p-0"
                                                        aria-label={m.embed_captions_remove_file()}
                                                        title={m.embed_captions_remove_file()}
                                                        onclick={() => removeFile(item.id)}
                                                        disabled={processing}>
                                                        <i class="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                                <label class="form-label small mb-1" for="customName-{item.id}">
                                                    {m.embed_captions_custom_name_label()}
                                                </label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    id="customName-{item.id}"
                                                    placeholder={m.embed_captions_custom_name_placeholder()}
                                                    bind:value={item.name}
                                                    disabled={processing}
                                                />
                                                {#if processing || item.uploadProgress > 0}
                                                    <div class="d-flex justify-content-between align-items-center mt-2 mb-1">
                                                        <span class="small">{m.embed_captions_video_upload()}</span>
                                                        <span class="badge bg-primary">{item.uploadProgress}%</span>
                                                    </div>
                                                    <div class="progress" style="height: 5px;">
                                                        <div class="progress-bar {item.status === 'failed' ? 'bg-danger' : 'bg-primary'}"
                                                            role="progressbar"
                                                            style="width: {item.uploadProgress}%"
                                                            aria-valuenow={item.uploadProgress}
                                                            aria-valuemin="0"
                                                            aria-valuemax="100">
                                                        </div>
                                                    </div>
                                                {/if}
                                            </li>
                                        {/each}
                                    </ul>
                                {/if}
                            </div>

                            <!-- Event Group Selection -->
                            <div class="mb-3">
                                <label for="eventGroupSelect" class="form-label fw-bold">
                                    {m.embed_captions_caption_track_label()}
                                </label>
                                <select 
                                    class="form-select" 
                                    id="eventGroupSelect" 
                                    bind:value={eventGroup}
                                    disabled={processing}
                                >
                                    <option value={null}>{m.embed_captions_caption_track_placeholder()}</option>
                                    {#each getValidEventGroups() as eg (eg.id)}
                                        <option value={eg.id}>{eg.name}</option>
                                    {/each}
                                </select>
                                <div class="form-text">{m.embed_captions_caption_track_help()}</div>
                            </div>

                            <div class="row">                               
                                <!-- Frame Rate -->
                                <div class="mb-3 col-md-4">
                                    <label for="frameRateSelect" class="form-label fw-bold">
                                        {m.embed_captions_frame_rate_label()}
                                    </label>
                                    <select 
                                        class="form-select" 
                                        id="frameRateSelect" 
                                        bind:value={trgFrameRate}
                                        disabled={processing}
                                        onchange={() => {
                                            if (parseFloat(trgFrameRate) === 29.97 || parseFloat(trgFrameRate) === 59.94) {
                                                trgDropFrame = "true";
                                            } else {
                                                trgDropFrame = "false";
                                            }
                                        }}
                                    >
                                        {#each frameRates.frameRates as frameRate (frameRate)}
                                            <option value={frameRate}>{frameRate}</option>
                                        {/each}
                                    </select>
                                    <div class="form-text">{m.embed_captions_frame_rate_help()}</div>
                                </div>

                                <!-- Drop Frame (only shown for 29.97 and 59.94) -->
                                {#if parseFloat(trgFrameRate) === 29.97 || parseFloat(trgFrameRate) === 59.94}
                                    <div class="mb-3 col-md-4">
                                        <label for="dropFrameSelect" class="form-label fw-bold">
                                            {m.embed_captions_drop_frame_label()}
                                        </label>
                                        <select 
                                            class="form-select" 
                                            id="dropFrameSelect" 
                                            bind:value={trgDropFrame}
                                            disabled={processing}
                                        >
                                            <option value="true">{m.embed_captions_yes()}</option>
                                            <option value="false">{m.embed_captions_no()}</option>
                                        </select>
                                        <div class="form-text">{m.embed_captions_drop_frame_help()}</div>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    </div>

                    {#if videoFiles.length > 0}
                        <div class="card shadow-sm mb-4">
                            <div class="card-header bg-light">
                                <h5 class="card-title mb-0">{m.embed_captions_pricing_summary()}</h5>
                            </div>
                            <div class="card-body">
                                {#each videoFiles as item (item.id)}
                                    <div class="mb-3">
                                        <div class="fw-bold text-truncate mb-1">{item.file.name}</div>
                                        <div class="row mb-1">
                                            <div class="col">
                                                <span class="fw-medium">{m.embed_captions_pricing_processing_label()}</span>
                                                <div class="text-muted small">{m.embed_captions_pricing_processing_help()}</div>
                                            </div>
                                            <div class="col-auto">
                                                <span class="fw-bold">${formatCents(EMBED_BASE_COST)}</span>
                                            </div>
                                        </div>
                                        <div class="row mb-1">
                                            <div class="col">
                                                <span class="fw-medium">{m.embed_captions_pricing_storage_label()}</span>
                                                <div class="text-muted small">{m.embed_captions_pricing_storage_help({ gb: item.billableGB })}</div>
                                            </div>
                                            <div class="col-auto">
                                                <span class="fw-bold">${formatCents(item.storageCost)}</span>
                                            </div>
                                        </div>
                                        {#if videoFiles.length > 1}
                                            <div class="row">
                                                <div class="col">
                                                    <span class="fw-medium">{m.embed_captions_pricing_subtotal_label()}</span>
                                                </div>
                                                <div class="col-auto">
                                                    <span class="fw-bold">${formatCents(item.cost)}</span>
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                                <hr class="my-2">
                                <div class="row">
                                    <div class="col">
                                        <span class="fw-bold fs-5">
                                            {videoFiles.length > 1
                                                ? m.embed_captions_pricing_grand_total_label({ count: videoFiles.length })
                                                : m.embed_captions_pricing_total_label()}
                                        </span>
                                    </div>
                                    <div class="col-auto">
                                        <span class="fw-bold fs-5 text-primary">${formatCents(grandTotal)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}

                    {#if processing}
                        <div class="card shadow-sm mb-3">
                            <div class="card-body">
                                <h6 class="card-title d-flex align-items-center">
                                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                                        <span class="visually-hidden">{m.embed_captions_processing_label()}</span>
                                    </div>
                                    {m.embed_captions_processing_job()}
                                </h6>
                                <div class="mb-1">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <span class="small">{m.embed_captions_overall_progress()}</span>
                                        <span class="badge bg-primary">{Math.round(progress)}%</span>
                                    </div>
                                    <div class="progress" style="height: 8px;">
                                        <div class="progress-bar progress-bar-striped progress-bar-animated" 
                                            role="progressbar" 
                                            style="width: {progress}%" 
                                            aria-valuenow={progress} 
                                            aria-valuemin="0" 
                                            aria-valuemax="100">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}

                    <div class="alert alert-warning small">
                        <i class="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>{m.embed_captions_important_label()}</strong> {m.embed_captions_important_text()} 
                        {#if videoFiles.length > 0}
                            <strong>{m.embed_captions_important_total({ total: formatCents(grandTotal) })}</strong>
                        {:else}
                            <strong>{m.embed_captions_important_pricing_pending()}</strong>
                        {/if}
                    </div>
                </form>
            </div>
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    onclick={hideModal}
                    disabled={processing}>{m.embed_captions_close_button()}</button>
                <button 
                    type="button"
                    class="btn btn-primary"
                    onclick={submitJob}
                    disabled={videoFiles.length === 0 || !eventGroup || processing}>
                    {#if processing}
                        <Circle size="16" color="#ffffff" unit="px" duration="1s"/>
                        <span class="ms-2">{m.embed_captions_processing_label()}</span>
                    {:else}
                        <i class="bi bi-cloud-upload me-2"></i>
                        {m.embed_captions_submit_button()}
                    {/if}
                </button>
            </div>
        </div>
    </div>
</div>

<style>
.progress {
    border-radius: 1rem;
    background-color: #e9ecef;
}

.text-truncate {
    max-width: 300px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}
</style>
